import { useCallback, useEffect, useMemo, useRef, useState, type ChangeEvent, type ReactNode } from "react";
import { revertLogoLabel } from "./centreLogoLabels";
import { HELP_ANCHORS } from "@keydials/ui";
import { sectionStatusPalette, sectionTakesHandSet, todaySectionKeyOf, unionStatusByKey } from "./instanceModel";
import { buildDialVM, drillTargetForSection, drillTargetForSegment, hasEmptyNodes, hideEmptyNodes, sectionTitle, type DrillTarget } from "./dialModel";
import { DialView } from "./components/DialView";
import { Icon } from "./components/Icon";
import { StatusDot } from "./components/primitives";
import { Legend, OverviewPanel, type PanelTab, SectionPanel } from "./workspace/panels";
import { midDegreesOf, rotationToEast, isMove } from "./workspace/dialRotation";
import { beamHalfAngle, beamOpacity, beamPoints, type BeamGeometry, type BeamLevel } from "./workspace/projectionBeam";
import { projectionColour } from "./workspace/cardProjection";
import { ActionPointPanel, DialAttributesBlock, hasDialAttributes, type AppointmentEditControls, type ItemEditControls } from "./workspace/ActionPointPanel";
import { ScopeHeader, ScopePill, scopeLevelWord, type ScopeLevel } from "./workspace/ScopeHeader";
import { segmentNoun } from "./vocabulary";
import { useCapabilities, useWorkspaceServices } from "./context";
import type { DialWorkspaceProps } from "./types";
import { useCompareMode } from "./workspace/compare/useCompareMode";
import { LookAheadButton, LookBackButton } from "./workspace/compare/LookBackButton";
import { ComingPanel } from "./workspace/compare/ComingPanel";
import { RangeBar } from "./workspace/compare/RangeBar";
import { Scrubber } from "./workspace/compare/Scrubber";
import { DialPair } from "./workspace/compare/DialPair";
import { ChangesPanel } from "./workspace/compare/ChangesPanel";
import { noHistoryYet, snapshotOfNow, thenDetail } from "./workspace/compare/compareModel";
import { CompareCard } from "./workspace/compare/CompareCard";
import { compareRows, type CompareLevel } from "./workspace/compare/compareRows";
import { useBringBack } from "./workspace/compare/useBringBack";
import { useMediaQuery } from "./lib/useMediaQuery";

type View = "overview" | "section" | "action";

const itemKeyOf = (sectionKey: string, segmentKey: string, itemKey: string, dimensionKey: string) =>
  `${sectionKey}|${segmentKey}|${itemKey}|${dimensionKey}`;


/**
 * One step of the breadcrumb, drawn as a pill (KEY-606): a bare grey word in a top bar was easy to miss as something
 * you can press, and the way back to the group is the move people reach for most. The current step is the filled
 * pill and is not a button; the steps you can go back to are outlined and lift on hover/focus.
 *
 * Long labels shrink a step and wrap onto a second line rather than being cut to a few characters; past two lines
 * they are clipped with an ellipsis, and the full text is always in the tooltip.
 */
function Crumb({ children, onClick, active, title }: { children: ReactNode; onClick?: () => void; active?: boolean; title?: string }) {
  const text = typeof children === "string" ? children : title ?? "";
  const fontSize = text.length > 40 ? 11.5 : text.length > 24 ? 12.5 : 13.5;
  return (
    <button
      onClick={onClick}
      disabled={active}
      title={title ?? (typeof children === "string" ? children : undefined)}
      aria-current={active ? "page" : undefined}
      className={active ? undefined : "kd-focusable kd-crumb"}
      style={{
        font: "inherit",
        fontSize,
        lineHeight: 1.2,
        fontWeight: active ? 700 : 600,
        border: active ? "var(--border-w) solid var(--accent-border)" : "var(--border-w) solid var(--border)",
        background: active ? "var(--accent-soft)" : "var(--surface)",
        cursor: active ? "default" : "pointer",
        color: active ? "var(--accent-text)" : "var(--text-muted)",
        padding: "5px 13px",
        borderRadius: 16,
        maxWidth: 240,
        textAlign: "left",
      }}
    >
      <span
        style={{
          display: "-webkit-box",
          WebkitBoxOrient: "vertical",
          WebkitLineClamp: 2,
          overflow: "hidden",
          overflowWrap: "anywhere",
        }}
      >
        {children}
      </span>
    </button>
  );
}

const chev = <Icon name="chevRight" size={14} style={{ color: "var(--text-faint)", opacity: 0.6 }} />;

// Longest section label shown in full in the breadcrumb; longer names fall back to the section's short key
// (mnemonic). Crumbs shrink and wrap onto two lines (KEY-606), so this is roughly what two small lines of a 240px
// pill hold before the clamp would cut it.
const SECTION_CRUMB_MAX = 60;

/** How long the ring takes to bring a selection round to East (KEY-532). From the design handoff. */
const ROTATE_MS = 700;

/** How long the dial takes to dive into the child it is opening (KEY-533). */
const DIVE_MS = 320;

/**
 * The navigable dial surface shared by the end-user workspace and the admin draft preview. It owns only
 * navigation/UI state; every side effect is delegated to the injected controller, and host services +
 * capabilities are read from <DialWorkspaceProvider>. Pass a `key` keyed on the dial id so switching
 * dials remounts it with fresh navigation state.
 */
export function DialWorkspace({
  detail,
  vm,
  history,
  itemHistory,
  subs,
  meUserId,
  meHasPhoneNumber,
  canSet,
  allocation,
  canEditDocs,
  canListDocs = true,
  canDownloadDocs = true,
  canEditAttributes,
  attributeAccess,
  canManageDocVersions,
  canPost,
  canModerate,
  restricted,
  canEditCentreLogo,
  centreLogoNote,
  lastUpdated,
  error,
  itemEditPolicy,
  controller,
  settingsSlot,
  initialPanelTab,
  overviewCrumbLabel,
  overviewTitle,
  overviewNote,
  parentCrumb,
  initialSectionKey,
  initialSegmentKey,
}: DialWorkspaceProps) {
  const caps = useCapabilities();
  const { api, getAccessToken } = useWorkspaceServices();
  const instanceId = detail.id;
  // KEY-539: this dial's word for an action point, threaded to every string below and down to the panels.
  // Memoised on the two fields rather than on `detail` so a detail refetch that changed a status doesn't
  // hand every child a new object.
  const noun = useMemo(
    () => segmentNoun(detail),
    [detail.segmentNounSingular, detail.segmentNounPlural],
  );

  // A drill-in from a group lands on the part of this dial the clicked node stood for: its section, and —
  // when a mirror-face segment mirrored one action point — that action point's panel. Seeded once at mount;
  // the host keys this component on the dial id, so switching dials reseeds.
  const onDial = (key: string | null | undefined) => !!key && vm.sections.some((s) => s.key === key);
  // KEY-775: with no drill-in target, a rolling-days dial opens on Today rather than the overview. A group's
  // focusSectionKey still wins; a dial with no days section gets null here and opens as it always did.
  const todayKey = todaySectionKeyOf(detail);
  const initialSection = onDial(initialSectionKey) ? initialSectionKey! : onDial(todayKey) ? todayKey : null;
  const initialSegment = initialSection && initialSegmentKey
    && vm.sections.find((s) => s.key === initialSection)?.segments.some((sg) => sg.key === initialSegmentKey)
    ? initialSegmentKey
    : null;
  const [view, setView] = useState<View>(initialSegment ? "action" : initialSection ? "section" : "overview");
  // Overview right-panel switcher (Direction B): Sections is the default tab. Seeded once, so a host
  // restoring "settings" from the URL doesn't fight the user's later clicks.
  const [panelTab, setPanelTab] = useState<PanelTab>(initialPanelTab ?? "sections");
  const [selSectionKey, setSelSectionKey] = useState<string | null>(initialSection);
  const [selSegmentKey, setSelSegmentKey] = useState<string | null>(initialSegment);
  // Direction B: open the first section's ring card by default (or the drill-in target's). Seeded once
  // at mount; a later reload after a status/item change won't fight the user's manual expand/collapse,
  // and switching dials remounts this component (host keys on the dial id) so it reseeds.
  const [expanded, setExpanded] = useState<Set<string>>(() => {
    const first = initialSection ?? vm.sections[0]?.key;
    return first ? new Set([first]) : new Set();
  });
  const [q, setQ] = useState("");
  const [showSearch, setShowSearch] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);

  // Centre-logo editor (dial admins): a small modal to upload a new hub image or revert to the definition's.
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [logoOpen, setLogoOpen] = useState(false);
  const [logoBusy, setLogoBusy] = useState(false);
  const [logoError, setLogoError] = useState<string | null>(null);

  const onLogoFile = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ""; // let the user re-pick the same file after a failure
    if (!file || !controller.onSetCentralImage) {
      return;
    }
    setLogoBusy(true);
    setLogoError(null);
    try {
      await controller.onSetCentralImage(file);
      setLogoOpen(false);
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : "Could not upload the image.");
    } finally {
      setLogoBusy(false);
    }
  };

  const revertLogo = async () => {
    if (!controller.onClearCentralImage) {
      return;
    }
    setLogoBusy(true);
    setLogoError(null);
    try {
      await controller.onClearCentralImage();
      setLogoOpen(false);
    } catch (err) {
      setLogoError(err instanceof Error ? err.message : "Could not revert the logo.");
    } finally {
      setLogoBusy(false);
    }
  };

  // "Hide empty action points" overview filter (itemised dials only). Seeded from and persisted to
  // KEY-457. Two gates, not one: the dial has to OFFER the filter (a dial-admin setting), and there has
  // to be something empty to hide, or the switch is dead. It used to be offered wherever a dial happened
  // to contain an empty node, so a dial could start hiding parts of itself because of how its data looked.
  //
  // The choice is the person's, and it is kept server-side rather than in localStorage: this product runs
  // on shared tablets, where a browser key is inherited by whoever picks the tablet up next. Optimistic —
  // the switch answers immediately and the write follows; a failed write leaves the next load authoritative.
  const canHideEmpty = useMemo(() => !!detail.hideEmptyEnabled && hasEmptyNodes(vm), [detail.hideEmptyEnabled, vm]);
  const [hideEmpty, setHideEmpty] = useState<boolean>(() => !!detail.hideEmptyForMe);
  useEffect(() => {
    setHideEmpty(!!detail.hideEmptyForMe);
  }, [detail.hideEmptyForMe]);
  const changeHideEmpty = useCallback(
    (next: boolean) => {
      setHideEmpty(next);
      const token = getAccessToken();
      if (token) {
        void api.setHideEmptyForMe(token, instanceId, next).catch(() => undefined);
      }
    },
    [api, getAccessToken, instanceId],
  );
  // The VM the dial + overview render from: with empty nodes stripped when the filter is on.
  const displayVm = useMemo(() => (hideEmpty && canHideEmpty ? hideEmptyNodes(vm) : vm), [hideEmpty, canHideEmpty, vm]);

  // Look back as a compare mode (Features:LookBackCompare): Then beside Now. Off on a group — group rollups
  // aren't recorded day by day — and in a preview, which has no past to replay. Neither the mode nor the tab
  // shows on a dial whose definition switched Look back off (KEY-735); absent means an older server, so on.
  const lookBackOffered = detail.lookBackEnabled !== false;
  const compareOffered = lookBackOffered && !!caps.lookBackCompare && caps.mode === "live";
  // KEY-890: Look ahead (Features:LookAhead) — the same side-by-side mode, looking forward.
  const lookAheadOffered = lookBackOffered && !!caps.lookAhead && caps.mode === "live";
  const compare = useCompareMode(instanceId, (compareOffered || lookAheadOffered) && !vm.isGroup);
  const lookingAhead = compare.active && compare.direction === "ahead";
  // The past face goes through the SAME model builder and the same empty-node filter as the live one, so
  // the two dials are laid out identically and a wedge on one sits exactly where it sits on the other.
  // KEY-760: a dial with no history yet (a brand-new one) has no past to replay — Then is Now. Said in the range
  // bar, with its controls disabled, and drawn from the live detail rather than a replay that has nothing in it.
  const compareEmpty = compare.active && !lookingAhead && noHistoryYet(compare.changeDays);
  const nowSnapshot = useMemo(() => (compareEmpty ? snapshotOfNow(detail) : null), [compareEmpty, detail]);
  const thenSnapshot = nowSnapshot ?? compare.snapshot;
  const thenLoading = !nowSnapshot && compare.status === "loading";
  const thenVm = useMemo(() => {
    if (!compare.active || !thenSnapshot) return null;
    const past = buildDialVM(thenDetail(detail, thenSnapshot, compare.days));
    return hideEmpty && canHideEmpty ? hideEmptyNodes(past) : past;
  }, [compare.active, thenSnapshot, detail, hideEmpty, canHideEmpty, compare.days]);
  // Two dials side by side and a card beside them only fit on a wide screen; narrower, the card goes under.
  const wide = useMediaQuery("(min-width: 1236px)");
  // The timeline needs room to be dragged along; below 920px the range bar is presets and a date (§3.3).
  const scrubberFits = useMediaQuery("(min-width: 920px)");

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (searchRef.current && !searchRef.current.contains(e.target as Node)) {
        setShowSearch(false);
      }
    };
    if (showSearch) {
      document.addEventListener("mousedown", handler);
    }
    return () => document.removeEventListener("mousedown", handler);
  }, [showSearch]);

  const statusName = useMemo(() => {
    const union = unionStatusByKey(detail);
    return (key: string) => union.get(key)?.name ?? key;
  }, [detail]);

  const itemStatusIndex = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of detail.itemStatuses ?? []) {
      map.set(itemKeyOf(s.sectionKey, s.segmentKey, s.itemKey, s.dimensionKey), s.statusKey);
    }
    return map;
  }, [detail]);

  // Both dynamic views behave identically — the 3D one only adds a transform, so every branch that asks
  // "is this the projection workspace?" must answer yes for both.
  const dynamic = caps.workspace === "dynamic" || caps.workspace === "dynamic3d";
  // KEY-532: how far the ring has been turned, cumulative. Held as state rather than derived, because
  // "shortest way from HERE" is a fact about where the dial currently is, not about what is selected — the
  // same pick reached from two directions turns two different ways.
  // KEY-775: a dial that opens on a selection (Today, or a group drill-in) starts already turned to it, so it
  // arrives at East rather than spinning there while the cards are hidden. Nothing on the dial leaves it at 0.
  const [rotationDeg, setRotationDeg] = useState(() => {
    const mid = dynamic ? midDegreesOf(displayVm.sections, initialSection, initialSegment) : null;
    return mid === null ? 0 : rotationToEast(mid, 0);
  });

  // The projection. `phase` hides the cards while the ring turns: a card that sits still while the thing
  // that projected it swings 180 degrees underneath has visibly nothing to do with it, and the whole point
  // of this mode is that the two are connected.
  const [phase, setPhase] = useState<"rotating" | "shown">("shown");
  const [diving, setDiving] = useState(false);

  // The turn is a CSS transition, so nothing fires when it lands — this is the clock that says it has. Keyed
  // on the rotation itself so a pick made mid-turn restarts it rather than letting the first timer cut the
  // second turn short.
  useEffect(() => {
    if (phase !== "rotating") {
      return;
    }
    const timer = setTimeout(() => setPhase("shown"), ROTATE_MS);
    return () => clearTimeout(timer);
  }, [phase, rotationDeg]);
  const tilt = caps.workspace === "dynamic3d";
  // Honour the OS setting: with reduced motion the dial still turns to the selection, it just arrives
  // rather than travelling. Read once per render, not cached, so a mid-session change is picked up.
  const reducedMotion = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;

  useEffect(() => {
    if (!dynamic) {
      // Classic never turns, and must not be left holding a stale angle if the mode is switched back.
      setRotationDeg(0);
      return;
    }
    const mid = midDegreesOf(displayVm.sections, selSectionKey, selSegmentKey);
    // Nothing selected leaves the dial where it is rather than snapping home: deselecting is a step back to
    // the overview, and a dial that unwinds on the way reads as the selection having been undone twice.
    if (mid === null) {
      return;
    }
    setRotationDeg((current) => {
      const next = rotationToEast(mid, current);
      // Re-picking the node already at East is not a turn — hiding the cards for 700ms to play an animation
      // that moves nothing would just be a flicker.
      setPhase(isMove(current, next) && !reducedMotion ? "rotating" : "shown");
      return next;
    });
  }, [dynamic, displayVm.sections, selSectionKey, selSegmentKey]);

  // KEY-532, the projection. `phase` hides the cards while the ring turns: a card that sits still while the
  // thing that projected it swings 180 degrees underneath has visibly nothing to do with it, and the whole
  // point of this mode is that the two are connected.
  const rowRef = useRef<HTMLDivElement | null>(null);
  const dialRef = useRef<HTMLDivElement | null>(null);
  const cardsRef = useRef<HTMLDivElement | null>(null);
  const [beamGeometry, setBeamGeometry] = useState<BeamGeometry | null>(null);

  // LAYOUT metrics (offsetLeft/offsetTop/offsetHeight), never getBoundingClientRect. The card stack plays an
  // entry animation that scales and slides it, and a rect reports the mid-animation position — which skews
  // the beam to wherever the cards happened to be on the frame it was measured.
  const measureBeam = useCallback(() => {
    const row = rowRef.current;
    const dial = dialRef.current;
    const cards = cardsRef.current;
    if (!row || !dial || !cards) {
      setBeamGeometry(null);
      return;
    }
    const offsetWithin = (el: HTMLElement) => {
      let left = 0;
      let top = 0;
      for (let node: HTMLElement | null = el; node && node !== row; node = node.offsetParent as HTMLElement | null) {
        left += node.offsetLeft;
        top += node.offsetTop;
      }
      return { left, top };
    };
    const d = offsetWithin(dial);
    const c = offsetWithin(cards);

    // The stack IS the card in this mode, and it is a fixed height — so the beam spans it directly and stops
    // moving. It used to be measured against the content, which changes size with every selection: the beam
    // was then sized to whatever was on screen when it was last measured and did not follow.
    setBeamGeometry({
      cx: d.left + dial.offsetWidth / 2,
      cy: d.top + dial.offsetHeight / 2,
      dialWidth: dial.offsetWidth,
      cardLeft: c.left,
      cardTop: c.top,
      cardBottom: c.top + cards.offsetHeight,
    });
  }, []);

  useEffect(() => {
    if (!dynamic) {
      setBeamGeometry(null);
      return;
    }
    measureBeam();
    window.addEventListener("resize", measureBeam);
    return () => window.removeEventListener("resize", measureBeam);
  }, [dynamic, measureBeam, view, selSectionKey, selSegmentKey, phase, compare.active, wide]);

  const selSection = vm.sections.find((s) => s.key === selSectionKey) ?? null;
  const selSegment = selSection?.segments.find((sg) => sg.key === selSegmentKey) ?? null;

  // What the beam and the framing are aimed at. Section-with-no-action-point is its own level, not a
  // half-measure of the other two — it is the middle rung of the ladder the card frames climb.
  const beamLevel: BeamLevel = selSegmentKey ? "segment" : selSectionKey ? "section" : "none";
  const projectionSelectionColour = selSegment?.colour ?? selSection?.status?.colour ?? null;
  const beamColour = projectionColour(beamLevel, projectionSelectionColour);
  const settingsView = view === "overview" && panelTab === "settings" && !compare.active;

  // Comparing on a narrower screen puts the card UNDER the dials, where a beam out of the east rim has
  // nothing to land on.
  const beam = dynamic && !settingsView && phase === "shown" && (!compare.active || wide)
    ? beamPoints(
        beamGeometry,
        beamLevel,
        beamHalfAngle(beamLevel, displayVm.sections.length, selSection?.segments.length ?? 0),
      )
    : null;

  // KEY-513: the level the panel column is showing, derived once so the header, the column's aria-label
  // and the "up one level" link cannot drift apart. Gated on `view` as well as the selection, for the same
  // reason the panels below are: `view` is what decides which panel is mounted, so reading anything else
  // here would let the header name a level the panel beside it isn't showing.
  const scopeLevel: ScopeLevel = view === "action" && selSegment ? "action" : view === "section" && selSection ? "section" : "dial";
  // detail.name, deliberately: overviewCrumbLabel is a literal ("Dial overview" in web-user) and
  // overviewTitle is the *definition's* name, neither of which is what this dial is called.
  const scopeName = scopeLevel === "action" ? selSegment!.name : scopeLevel === "section" ? sectionTitle(selSection!) : detail.name;

  const scopeParentName = scopeLevel === "action" ? sectionTitle(selSection!) : scopeLevel === "section" ? detail.name : null;
  // Both branches are wrapped rather than passed by reference: openSection and goOverview are consts
  // declared further down, so naming one here evaluates it before it exists.
  const goUp = scopeLevel === "action"
    ? () => openSection(selSection!.key)
    : scopeLevel === "section"
      ? () => goOverview()
      : undefined;
  // The chip, and beside it the "in <parent>" link the strip used to hold. That link is the only PERMANENT
  // way up a level — the breadcrumb is the other one and it scrolls away — so it moves with the chip rather
  // than going with the card.
  //
  // KEY-573: it shrinks, ellipsing the link, rather than holding its full width. At `flex: none` a narrow
  // column pushed it under the section's status pill beside it, which then painted straight over the link.
  const scopePill = dynamic ? (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 8, flex: "0 1 auto", minWidth: 0, maxWidth: "100%" }}>
      <ScopePill level={scopeLevel} noun={noun} />
      {scopeParentName && goUp && (
        <button
          type="button"
          onClick={goUp}
          title={`Back to ${scopeParentName}`}
          style={{
            font: "inherit", background: "none", border: "none", padding: 0, cursor: "pointer",
            color: "var(--text-faint)", fontSize: 12.5, fontWeight: 600, whiteSpace: "nowrap",
            maxWidth: 220, minWidth: 0, overflow: "hidden", textOverflow: "ellipsis",
          }}
        >
          in {scopeParentName}
        </button>
      )}
    </span>
  ) : undefined;


  // Look back: the card can follow the Then dial. It then shows the statuses as they were, read-only — a
  // past can't be edited, and a control that looked live on a Then card would write to NOW. Fields are
  // still today's until the as-of read carries them; the notice above the card says so.
  const followThen = compare.active && compare.follows === "then" && !!thenVm;
  const cardVm = followThen ? thenVm! : displayVm;
  const cardApVm = followThen ? thenVm! : vm;
  const cardSection = followThen ? thenVm!.sections.find((s) => s.key === selSectionKey) ?? selSection : selSection;
  const cardSegment = followThen ? cardSection?.segments.find((sg) => sg.key === selSegmentKey) ?? selSegment : selSegment;
  const cardItemStatusIndex = useMemo(() => {
    if (!followThen) return itemStatusIndex;
    const map = new Map<string, string>();
    for (const s of thenVm!.detail.itemStatuses ?? []) {
      map.set(itemKeyOf(s.sectionKey, s.segmentKey, s.itemKey, s.dimensionKey), s.statusKey);
    }
    return map;
  }, [followThen, thenVm, itemStatusIndex]);
  // Look back's card: Then beside Now for wherever the card is, with Bring back. Drawn from the two view
  // models the dials are drawn from — so a row and the wedge above it can never disagree about a status.
  const bring = useBringBack(instanceId, compare.day, controller.onRefresh);
  const compareWhere: CompareLevel =
    view === "action" && selSection && selSegment ? { level: "action", sectionKey: selSection.key, segmentKey: selSegment.key }
    : view === "section" && selSection ? { level: "section", sectionKey: selSection.key }
    : { level: "dial" };
  const compareCardRows = useMemo(
    () => compare.active && thenSnapshot && thenVm
      ? compareRows(compareWhere, displayVm, thenVm, thenSnapshot, {
        canSet,
        canEditAttributes: canEditAttributes ?? canEditDocs,
        attributeAccess,
      })
      : [],
    // compareWhere is rebuilt every render; its three inputs are what it depends on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [compare.active, thenSnapshot, thenVm, displayVm, view, selSection?.key, selSegment?.key, canSet, canEditAttributes, canEditDocs, attributeAccess],
  );
  const compareOverview = compare.active ? (
    <CompareCard
      ahead={lookingAhead}
      rows={compareCardRows}
      day={compare.day}
      bring={bring}
      loading={!thenSnapshot || thenLoading}
    />
  ) : undefined;
  const changesTab = lookingAhead ? (
    <ComingPanel
      detail={detail}
      changes={compare.changes}
      day={compare.day}
      scope={
        view === "action" && selSection && selSegment ? { sectionKey: selSection.key, segmentKey: selSegment.key }
        : view === "section" && selSection ? { sectionKey: selSection.key }
        : undefined
      }
      scopeName={scopeName}
      statusName={statusName}
    />
  ) : compare.active ? (
    <ChangesPanel
      instanceId={instanceId}
      day={compare.day}
      scope={
        view === "action" && selSection && selSegment ? { sectionKey: selSection.key, segmentKey: selSegment.key }
        : view === "section" && selSection ? { sectionKey: selSection.key }
        : undefined
      }
      scopeName={scopeName}
      onOpenActivity={() => {
        compare.leave();
        goOverview();
        setPanelTab("activity");
      }}
    />
  ) : undefined;

  // The Settings tab is the one place the dial earns nothing: none of its cards are reached by
  // clicking the dial, so the column is dropped and the panel takes the full width (KEY-496).

  const openSection = (sectionKey: string) => {
    setSelSectionKey(sectionKey);
    setSelSegmentKey(null);
    setView("section");
    setQ("");
    setShowSearch(false);
  };
  const openAp = (sectionKey: string, segmentKey: string) => {
    setSelSectionKey(sectionKey);
    setSelSegmentKey(segmentKey);
    setView("action");
    setQ("");
    setShowSearch(false);
  };
  // A drill-in opens the child dial at the part the clicked node stood for — omitting an all-null target so
  // the host's nav state stays clean when there is nothing to land on.
  const drillTo = (target: DrillTarget) => {
    const { childInstanceId, ...where } = target;
    const go = () => controller.onDrillToChild?.(childInstanceId, where.sectionKey ? where : undefined);
    // KEY-533: the dial dives into the child it is opening, so a drill-in reads as going INTO something
    // rather than as the page simply changing. In every workspace mode since KEY-572, like the rest of the
    // group cues; skipped for anyone who has asked for less motion — and `go` still runs either way, because
    // the navigation is the point and the animation is decoration. A stuck flag would strand the dial
    // invisible, so nothing here gates on it resolving.
    if (reducedMotion) {
      go();
      return;
    }
    setDiving(true);
    window.setTimeout(() => {
      setDiving(false);
      go();
    }, DIVE_MS);
  };
  // On a Group Key Dial, selecting an overview segment drills into its child instance (when allowed)
  // instead of opening a status changer; otherwise it opens the action-point panel as usual.
  const selectSegment = (sectionKey: string, segmentKey: string) => {
    if (vm.isGroup) {
      const target = drillTargetForSegment(vm, sectionKey, segmentKey);
      if (target) {
        if (caps.groupDrillIn) {
          drillTo(target);
        }
        return;
      }
    }
    openAp(sectionKey, segmentKey);
  };
  // On a Group Key Dial, picking a section drills straight into its child dial — the section IS that dial,
  // so opening the group's own section panel would just be a list of links to the same place.
  const selectSection = (sectionKey: string) => {
    if (vm.isGroup && caps.groupDrillIn) {
      const target = drillTargetForSection(vm, sectionKey);
      if (target) {
        drillTo(target);
        return;
      }
    }
    openSection(sectionKey);
  };
  const goOverview = () => {
    setView("overview");
    setSelSectionKey(null);
    setSelSegmentKey(null);
  };
  // KEY-751: the host moving onto a settings URL while this dial is already open (the "Who's on this dial?"
  // prompt's Add people) has to show that tab too. Only a CHANGE of the prop does it, so the seed above still
  // never fights the user's own tab clicks.
  const lastInitialPanelTab = useRef(initialPanelTab);
  useEffect(() => {
    if (initialPanelTab && initialPanelTab !== lastInitialPanelTab.current) {
      goOverview();
      setPanelTab(initialPanelTab);
    }
    lastInitialPanelTab.current = initialPanelTab;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialPanelTab]);
  // KEY-702: the dial's own band toggles, as its wedges already do — re-picking the section that is OPEN
  // (section level, no action point) steps back to the whole dial. Gated on `view`, like scopeLevel: at
  // action level the band is the parent of what is open, and picking it still goes up one level to the
  // section. Only the rings use this; the search rows and the Sections list keep `selectSection`, because
  // "open Alpha" from a list must open Alpha even when it already is.
  const pickSection = (sectionKey: string) =>
    view === "section" && selSectionKey === sectionKey ? goOverview() : selectSection(sectionKey);

  const results = useMemo(() => {
    if (!q.trim()) {
      return null;
    }
    const t = q.toLowerCase();
    const sections = vm.sections.filter((s) => s.label.toLowerCase().includes(t));
    const segments: { section: (typeof vm.sections)[number]; segment: (typeof vm.sections)[number]["segments"][number] }[] = [];
    for (const section of vm.sections) {
      for (const segment of section.segments) {
        if (segment.name.toLowerCase().includes(t)) {
          segments.push({ section, segment });
        }
      }
    }
    return { sections, segments };
  }, [vm, q]);

  // The action-point Items tab's add/remove/rename controls, assembled from the host policy + controller.
  const itemEdit: ItemEditControls | undefined =
    caps.itemEditing && itemEditPolicy && selSegment
      ? {
          canAdd: itemEditPolicy.canAdd,
          canRemoveCustom: itemEditPolicy.canRemoveCustom,
          canHideBase: itemEditPolicy.canHideBase,
          canRenameCustom: itemEditPolicy.canRenameCustom,
          canRenameBase: itemEditPolicy.canRenameBase,
          maxItemsPerSegment: itemEditPolicy.maxItemsPerSegment,
          onAdd: () => controller.onAddItem?.(selSegment.key, selSection?.key) ?? Promise.resolve(null),
          onRemove: (itemKey, mode) => controller.onRemoveItem?.(itemKey, mode),
          onRename: (itemKey, name, isCustom) => controller.onRenameItem?.(itemKey, name, isCustom),
          onSetSpan: controller.onSetItemSpan,
        }
      : undefined;

  // KEY-821: the card's appointment controls, when the host can change the viewer's own appointments.
  const appointmentEdit: AppointmentEditControls | undefined =
    controller.onEditAppointment && controller.onDeleteAppointment
      ? { onEdit: controller.onEditAppointment, onDelete: controller.onDeleteAppointment }
      : undefined;

  return (
    // The side padding shrinks on a phone: a fixed 28px a side left too little for the dial at 320px.
    <div style={{ maxWidth: 1240, margin: "0 auto", padding: "26px clamp(16px, 4vw, 28px) 80px" }}>
      {/* breadcrumb */}
      <nav aria-label="Breadcrumb" style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 7, rowGap: 8, marginBottom: 16 }}>
        {controller.onBackToList && (
          <>
            <button
              onClick={() => controller.onBackToList?.()}
              style={{
                font: "inherit",
                display: "inline-flex",
                alignItems: "center",
                gap: 5,
                background: "var(--surface)",
                border: "var(--border-w) solid var(--border)",
                borderRadius: 16,
                color: "var(--text-muted)",
                fontSize: 13.5,
                fontWeight: 600,
                cursor: "pointer",
                padding: "5px 13px 5px 9px",
              }}
              className="kd-focusable kd-crumb"
            >
              <Icon name="back" size={15} />
              All dials
            </button>
            {chev}
          </>
        )}
        {parentCrumb && (
          <>
            <Crumb onClick={parentCrumb.onClick} title={parentCrumb.title ?? parentCrumb.label}>
              {parentCrumb.label}
            </Crumb>
            {chev}
          </>
        )}
        <Crumb onClick={goOverview} active={view === "overview"}>
          {overviewCrumbLabel ?? detail.name}
        </Crumb>
        {(view === "section" || view === "action") && selSection && (
          <>
            {chev}
            <Crumb
              onClick={() => openSection(selSection.key)}
              active={view === "section"}
              title={selSection.label}
            >
              {selSection.showMnemonic || selSection.label.length > SECTION_CRUMB_MAX ? selSection.key : selSection.label}
            </Crumb>
          </>
        )}
        {view === "action" && selSegment && (
          <>
            {chev}
            <Crumb active>{selSegment.name}</Crumb>
          </>
        )}
      </nav>

      {error && (
        <p
          style={{
            marginBottom: 16,
            background: "var(--surface)",
            border: "var(--border-w) solid var(--border)",
            borderRadius: "var(--radius)",
            padding: "10px 14px",
            color: "var(--st-overdue)",
            fontSize: 13.5,
          }}
        >
          {error}
        </p>
      )}

      {/* search, and beside it Look back — which changes the whole workspace, so it lives up here rather
          than as a tab in one card */}
      <div style={{ display: "flex", gap: 10, alignItems: "stretch", marginBottom: compare.active ? 12 : 22 }}>
      <div style={{ position: "relative", flex: 1, minWidth: 0 }} ref={searchRef}>
        <Icon name="search" size={18} style={{ position: "absolute", left: 15, top: "50%", transform: "translateY(-50%)", color: "var(--text-faint)" }} />
        <input
          value={q}
          onChange={(e) => {
            setQ(e.target.value);
            setShowSearch(e.target.value.trim().length > 0);
          }}
          onFocus={() => setShowSearch(q.trim().length > 0)}
          placeholder={`Search sections and ${noun.many}…`}
          className="kd-focusable"
          style={{
            width: "100%",
            padding: "13px 16px 13px 44px",
            borderRadius: "var(--radius)",
            border: "var(--border-w) solid var(--border)",
            background: "var(--surface)",
            color: "var(--text)",
            font: "inherit",
            fontSize: 14.5,
            outline: "none",
          }}
        />
        {showSearch && results && (
          <div
            style={{
              position: "absolute",
              zIndex: 40,
              width: "100%",
              marginTop: 8,
              background: "var(--surface)",
              borderRadius: "var(--radius)",
              border: "var(--border-w) solid var(--border)",
              boxShadow: "var(--shadow-lg)",
              maxHeight: 360,
              overflowY: "auto",
              padding: 8,
            }}
          >
            {results.sections.map((s) => (
              <button key={s.key} onClick={() => selectSection(s.key)} className="kd-row-hover" style={searchRow}>
                <Icon name="layers" size={15} style={{ color: "var(--text-faint)" }} />
                <span title={s.label} style={{ fontWeight: 600, fontSize: 14 }}>{sectionTitle(s)}</span>
                <span style={{ fontSize: 12, color: "var(--text-faint)", marginLeft: "auto" }}>Section</span>
              </button>
            ))}
            {results.segments.map(({ section, segment }) => (
              <button key={`${section.key}/${segment.key}`} onClick={() => selectSegment(section.key, segment.key)} className="kd-row-hover" style={searchRow}>
                <StatusDot colour={segment.colour} size={9} />
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{segment.name}</div>
                  <div title={section.label} style={{ fontSize: 12, color: "var(--text-faint)" }}>in {sectionTitle(section)}</div>
                </div>
              </button>
            ))}
            {results.sections.length === 0 && results.segments.length === 0 && (
              <div style={{ padding: 24, textAlign: "center", color: "var(--text-faint)", fontSize: 14 }}>Nothing matches “{q}”.</div>
            )}
          </div>
        )}
      </div>
        {lookAheadOffered && (
          <LookAheadButton
            active={lookingAhead}
            day={compare.day}
            disabled={!!vm.isGroup}
            onEnter={() => {
              setPanelTab("overview");
              compare.enter("ahead");
            }}
            onLeave={compare.leave}
          />
        )}
        {compareOffered && (
          <LookBackButton
            active={compare.active && !lookingAhead}
            day={compare.day}
            disabled={!!vm.isGroup}
            onEnter={() => {
              // The comparison is what the mode is for, and it lives on Overview: open there, rather than
              // on whichever tab was up (the list, or a Settings/Discussion tab compare mode doesn't show).
              setPanelTab("overview");
              compare.enter("back");
            }}
            onLeave={compare.leave}
          />
        )}
      </div>
      {compare.active && (
        <RangeBar
          direction={compare.direction}
          day={compare.day}
          status={compare.status}
          onDay={compare.setDay}
          onRetry={compare.retry}
          empty={compareEmpty}
          scrubber={scrubberFits && !lookingAhead && compare.changeDays && !compareEmpty ? (
            <Scrubber
              value={{ then: compare.day }}
              changeDays={compare.changeDays}
              onPreview={compare.previewDay}
              loading={compare.status === "loading"}
            />
          ) : undefined}
        />
      )}

      {/* KEY-457: say so, above the dial, whenever the filter is actually removing something. A dial that
          quietly shows less than it holds is the failure mode worth guarding against — somebody reads a
          full-looking dial and doesn't know there are action points behind it. Only shown when the filter
          is on AND there is something hidden, so it never nags on a dial with nothing empty. */}
      {hideEmpty && canHideEmpty && (
        <div
          role="status"
          style={{
            display: "flex", alignItems: "center", gap: 9, flexWrap: "wrap",
            padding: "9px 13px", marginBottom: 14, borderRadius: "var(--radius-sm)",
            background: "var(--surface-inset)", border: "var(--border-w) solid var(--border)",
            fontSize: 13, color: "var(--text-muted)",
          }}
        >
          <Icon name="ban" size={15} style={{ flex: "none", color: "var(--text-faint)" }} />
          <span>Empty {noun.many} are hidden, so this dial is showing less than it holds.</span>
          <button
            type="button"
            onClick={() => changeHideEmpty(false)}
            style={{
              font: "inherit", fontSize: 13, fontWeight: 700, background: "none", border: "none",
              color: "var(--accent-text)", cursor: "pointer", padding: 0, textDecoration: "underline",
            }}
          >
            Show everything
          </button>
        </div>
      )}

      {/* two columns, or one on Settings. data-view lets the CSS follow: every other view keeps the
          dial beside the panel, Settings drops it and gives the panel the whole width. */}
      <div
        className="kd-split"
        data-view={settingsView ? "settings" : view}
        data-workspace={dynamic ? "dynamic" : undefined}
        data-tilt={tilt ? "on" : undefined}
        data-compare={compare.active ? "on" : undefined}
        ref={rowRef}
      >
        {/* The beam sits BEHIND both columns (z-index 0 against their 1), so its inner end is hidden by the
            opaque dial and it reads as light leaving the rim rather than as a shape drawn over it. */}
        {beam && (
          <svg
            style={{ position: "absolute", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "visible" }}
            aria-hidden="true"
          >
            <defs>
              <linearGradient id="kd-beam-grad" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0" stopColor={beamColour} stopOpacity={beamOpacity(beamLevel)} />
                <stop offset="0.35" stopColor={beamColour} stopOpacity={0.16} />
                <stop offset="1" stopColor={beamColour} stopOpacity={0.05} />
              </linearGradient>
            </defs>
            <polygon points={beam} fill="url(#kd-beam-grad)" style={{ animation: "kd-beam .45s ease both" }} />
          </svg>
        )}
        {!settingsView && compare.active && (
          <div className="kd-dial-col" style={dynamic ? { position: "relative", zIndex: 1 } : undefined}>
            <DialPair
              direction={compare.direction}
              thenVm={thenVm}
              nowVm={displayVm}
              day={compare.day}
              loading={thenLoading}
              follows={compare.follows}
              onFollows={compare.setFollows}
              selectedSectionKey={selSectionKey}
              selectedSegmentKey={selSegmentKey}
              // ONE angle for both, computed once from the one selection: the two dials cannot drift.
              rotationDeg={dynamic ? rotationDeg : 0}
              rotationMs={dynamic && !reducedMotion ? ROTATE_MS : 0}
              showTie={dynamic && wide && beamLevel !== "none" && phase === "shown"}
              onSelectSection={pickSection}
              onSelectSegment={(secK, segK) => (segK ? selectSegment(secK, segK) : goOverview())}
              nowDialRef={dialRef}
              onEditCentreLogo={
                canEditCentreLogo
                  ? () => {
                      setLogoError(null);
                      setLogoOpen(true);
                    }
                  : undefined
              }
              centreLogoNote={canEditCentreLogo ? undefined : centreLogoNote}
              legend={<Legend vm={displayVm} showProgress selSegment={selSegment} selSection={selSection} />}
            />
          </div>
        )}
        {!settingsView && !compare.active && (
          <div className="kd-dial-col" style={dynamic ? { position: "relative", zIndex: 1 } : undefined}>
            <div
              ref={dialRef}
              style={{
                width: "100%",
                maxWidth: 500,
                aspectRatio: "1",
                margin: "0 auto",
                ...(diving ? { animation: `kd-dive ${DIVE_MS}ms ease-in both` } : {}),
              }}
            >
              <DialView
                vm={displayVm}
                progress
                selectedSectionKey={selSectionKey}
                selectedSegmentKey={selSegmentKey}
                rotationDeg={dynamic ? rotationDeg : 0}
                rotationMs={dynamic && !reducedMotion ? ROTATE_MS : 0}
                // KEY-533, and KEY-572 for Classic: only on a group, because the cues say "this dial contains
                // dials", which is a lie anywhere else. Every workspace mode, because being a group is a fact
                // about the dial, not about the layout; Classic-only users read a group as a plain dial.
                groupCues={!!detail.isGroup}
                onSelectSection={pickSection}
                onSelectSegment={(secK, segK) => (segK ? selectSegment(secK, segK) : goOverview())}
                onEditCentreLogo={
                  canEditCentreLogo
                    ? () => {
                        setLogoError(null);
                        setLogoOpen(true);
                      }
                    : undefined
                }
                centreLogoNote={canEditCentreLogo ? undefined : centreLogoNote}
              />
            </div>
            {/* displayVm, not vm: the key describes what is on the dial, and hide-empty may have
                taken nodes off it. selSegment narrows it to the chosen action point's own scale
                (KEY-495). */}
            <Legend vm={displayVm} showProgress selSegment={selSegment} selSection={selSection} />
          </div>
        )}

        <div
          ref={cardsRef}
          key={dynamic ? `${scopeLevel}:${scopeName}` : undefined}
          data-projection={dynamic && !settingsView ? beamLevel : undefined}
          style={{
            ...(dynamic ? ({ "--kd-projection": beamColour } as Record<string, string>) : {}),
            minWidth: 0,
            display: "flex",
            flexDirection: "column",
            gap: "var(--gap-stack)",
            ...(dynamic ? { position: "relative", zIndex: 1, transformOrigin: "left center" } : {}),
            // Hidden rather than unmounted while the ring turns: unmounting would drop scroll position and
            // any half-typed note in a card. `key` above is what replays the entry animation on each change
            // of selection — without it React reuses the node and the animation never re-runs.
            ...(dynamic && phase === "rotating" ? { visibility: "hidden" as const } : {}),
            // Settings drops the dial column entirely (KEY-496), so there is nothing projecting anything:
            // no beam, no frame, no fixed-height box, and no entry animation either.
            ...(dynamic && !settingsView && phase === "shown" && !reducedMotion
              ? { animation: "kd-project .5s cubic-bezier(.2,.8,.2,1) both" }
              : {}),
          }}
          onAnimationEnd={dynamic ? measureBeam : undefined}
          role="region"
          aria-label={`${scopeLevelWord(noun)[scopeLevel]}: ${scopeName}`}
        >
          {/* KEY-513: which level is open. The three panels are otherwise near-identical cards, and none of
              them says its own level out loud — so before KEY-513 the only tell that a click had changed
              level was which heading happened to be biggest. */}
          {/* Classic keeps the strip. Dynamic drops it and puts the chip inside the panel's own card
              (KEY-532): being a card in its own right, the strip is what the projection's frame and beam
              landed on — lighting up a one-line label instead of the thing it was describing. */}
          {!dynamic && (
            <ScopeHeader
              level={scopeLevel}
              name={scopeName}
              parentName={scopeParentName}
              onGoUp={goUp}
              colour={scopeLevel === "action" ? selSegment!.colour : null}
              sectionColour={selSection?.colour}
              noun={noun}
            />
          )}
          {followThen && (
            <p className="kd-then-notice" role="status">
              <Icon name="history" size={15} />
              <span>
                The card is following <strong>Then</strong>: statuses and fields as they were, read-only.
              </span>
              <button type="button" className="kd-focusable" onClick={() => compare.setFollows("now")}>Follow Now</button>
            </p>
          )}
          {view === "overview" && (
            // KEY-573: no wrapper around the panel. The Dynamic frame flattens only its own children's and
            // grandchildren's cards, so one extra div left every whole-dial card a bordered card INSIDE the
            // frame, and left the tab strip and the child list with nothing padding them off its edge.
            <OverviewPanel
              scopePill={scopePill}
              // The frame exists exactly when `data-projection` is set above; the panel pads its loose blocks
              // (the tab row, the list) into cards only then, so Classic keeps its floating pill strip.
              framed={dynamic && !settingsView}
              vm={cardVm}
              instanceId={instanceId}
              panelTab={panelTab}
              setPanelTab={setPanelTab}
              expanded={expanded}
              setExpanded={setExpanded}
              onOpenSection={selectSection}
              onOpenAp={selectSegment}
              lastUpdated={lastUpdated ?? null}
              hideEmpty={hideEmpty}
              setHideEmpty={changeHideEmpty}
              showEmptyToggle={canHideEmpty}
              // KEY-537: the tab goes entirely, not empty. The server returns nothing here for a
              // list-denied reader, and an empty table is a dead end that explains itself as a bug.
              showFilesTab={caps.documents && canListDocs}
              canDownloadDocs={canDownloadDocs}
              showMembersTab={caps.mode === "live"}
              showActivityTab={caps.mode === "live"}
              showComingUpTab={!vm.isGroup}
              // With the compare mode on, Look back is the button on the search row, not a tab.
              showLookBackTab={lookBackOffered && !vm.isGroup && caps.mode === "live" && !compareOffered}
              compareChanges={changesTab}
              compareOverview={compareOverview}
              // KEY-559: a transcript of real messages, so only where messaging is live.
              showDiscussionTab={caps.messages && caps.mode === "live"}
              meUserId={meUserId}
              subs={subs}
              onSubsChanged={controller.onSubsChanged}
              meHasPhoneNumber={meHasPhoneNumber}
              showSubscribe={caps.mode === "live"}
              aboutSlot={
                /* KEY-434: the dial's own fields — an emergency contact, a site address. KEY-485 moved
                   them onto their own About tab beside Overview: as a sibling of the panel they rendered
                   under whichever tab was open, so a site address hung below the Files list. Passed as a
                   slot rather than a flag because the tab only exists when the dial declares such a
                   field, which the great majority do not. */
                hasDialAttributes(detail, attributeAccess) ? (
                  <DialAttributesBlock
                    detail={detail}
                    canEdit={canEditAttributes ?? canEditDocs}
                    access={attributeAccess}
                    onSet={controller.onSetNodeAttribute}
                    card
                  />
                ) : undefined
              }
              settingsSlot={settingsSlot}
              overviewTitle={overviewTitle}
              overviewNote={overviewNote}
              /* KEY-529: the Subdials tab opens a child through the SAME drill-in the wedges use, so a
                 child reached from the list and one reached from the ring arrive identically — the dive
                 animation included. Landing on the child's overview, since a row stands for the whole
                 child dial rather than for one part of it the way a wedge does. */
              onOpenChild={(childInstanceId) => drillTo({ childInstanceId, sectionKey: null, segmentKey: null })}
              /* KEY-567: a child's thread in the group's Discussion tab opens where it was said, through that
                 same drill-in. Without drill-in the row simply has no button, rather than one that goes nowhere. */
              onOpenChildAp={
                caps.groupDrillIn
                  ? (childInstanceId, sectionKey, segmentKey) => drillTo({ childInstanceId, sectionKey, segmentKey })
                  : undefined
              }
            />
          )}
          {view === "section" && selSection && cardSection && (
            <SectionPanel
              scopePill={scopePill}
              instanceId={instanceId}
              section={cardSection}
              compareChanges={changesTab}
              compareOverview={compareOverview}
              onOpenAp={(segK) => selectSegment(selSection.key, segK)}
              subs={subs}
              onSubsChanged={controller.onSubsChanged}
              meHasPhoneNumber={meHasPhoneNumber}
              // A section's own status (KEY-479): only where the definition offers it and the host can
              // write it, never on a fed section (not this dial's to change). Gated on the SECTION scope —
              // an empty segment key — so a grant on one action point inside it does not reach the section.
              sectionStatus={
                detail.sectionStatusEnabled && controller.onSetSectionStatus && !selSection.fedFrom && !followThen
                  && sectionTakesHandSet(detail, selSection.key)
                  ? {
                      statuses: sectionStatusPalette(detail, selSection.key),
                      canSet: (status) => canSet(status, selSection.key, ""),
                      onPick: (statusKey) => controller.onSetSectionStatus!(selSection.key, statusKey),
                    }
                  : null
              }
              detail={detail}
              canEditAttributes={!followThen && (canEditAttributes ?? canEditDocs)}
              attributeAccess={attributeAccess}
              onSetNodeAttribute={controller.onSetNodeAttribute}
              canEditDocs={!followThen && canEditDocs}
              canManageDocVersions={!followThen && canManageDocVersions}
              showFilesTab={caps.documents && canListDocs}
              canDownloadDocs={canDownloadDocs}
              showActivityTab={caps.mode === "live"}
              showDiscussionTab={caps.messages && caps.mode === "live"}
              meUserId={meUserId}
            />
          )}
          {view === "action" && selSection && selSegment && cardSection && cardSegment && (
            <ActionPointPanel
              scopePill={scopePill}
              vm={cardApVm}
              section={cardSection}
              segment={cardSegment}
              compareChanges={changesTab}
              compareOverview={compareOverview}
              fedFrom={selSection.fedFrom ?? null}
              itemStatusIndex={cardItemStatusIndex}
              history={history}
              itemHistory={itemHistory}
              statusName={statusName}
              // A fed section (KEY-257 phase 3) is watched, not worked: every edit affordance is withheld
              // whatever the viewer's role here, because it is not their dial to change — the write would
              // be refused, and offering a control that always fails is worse than not offering it.
              canSet={selSection.fedFrom || followThen ? () => false : canSet}
              allocation={selSection.fedFrom || followThen ? undefined : allocation}
              onRefresh={controller.onRefresh}
              canEditDocs={!selSection.fedFrom && !followThen && canEditDocs}
              // Not conditioned on fedFrom, unlike every edit gate beside it: this is a READ, and a fed
              // section is watched rather than worked — watching is precisely what it is for.
              canListDocs={canListDocs}
              // Withheld on a fed section for the same reason every other edit affordance is: it is not
              // this dial's tag list to add to. Undefined when the host offers no writer at all (the admin
              // draft preview), which is what hides the "+ Add a tag" pill there.
              onCreateDocumentTag={selSection.fedFrom || followThen ? undefined : controller.onCreateDocumentTag}
              canEditAttributes={!selSection.fedFrom && !followThen && (canEditAttributes ?? canEditDocs)}
              attributeAccess={attributeAccess}
              canManageDocVersions={!selSection.fedFrom && !followThen && canManageDocVersions}
              onSetSegmentStatus={(statusKey, review) => controller.onSetSegmentStatus(selSection.key, selSegment.key, statusKey, review)}
              onScheduleRevert={(reviewDate, revertToStatusKey, leadDays, repeatDays, recurUnit, recurCount) =>
                controller.onScheduleRevert(selSection.key, selSegment.key, reviewDate, revertToStatusKey, leadDays, repeatDays, recurUnit, recurCount)}
              onSetItemStatus={(itemKey, dimensionKey, statusKey, review) =>
                controller.onSetItemStatus(selSection.key, selSegment.key, itemKey, dimensionKey, statusKey, review)}
              onScheduleItemRevert={(itemKey, dimensionKey, reviewDate, revertToStatusKey, leadDays, repeatDays, recurUnit, recurCount) =>
                controller.onScheduleItemRevert(selSection.key, selSegment.key, itemKey, dimensionKey, reviewDate, revertToStatusKey, leadDays, repeatDays, recurUnit, recurCount)}
              onSetNodeAttribute={controller.onSetNodeAttribute}
              onOpenSection={() => openSection(selSection.key)}
              onOpenSegment={(segmentKey) => setSelSegmentKey(segmentKey)}
              subs={subs}
              onSubsChanged={controller.onSubsChanged}
              meUserId={meUserId}
              meHasPhoneNumber={meHasPhoneNumber}
              canPost={!selSection.fedFrom && !followThen && canPost}
              restricted={restricted}
              canModerate={canModerate}
              itemEdit={followThen ? undefined : itemEdit}
              appointmentEdit={followThen ? undefined : appointmentEdit}
              onSetUploadAutomation={
                controller.onSetUploadAutomation && !followThen
                  ? (itemKey, dimensionKey, enabled, statusKey) =>
                      controller.onSetUploadAutomation!(selSection.key, selSegment.key, itemKey, dimensionKey, enabled, statusKey)
                  : undefined
              }
            />
          )}
        </div>
      </div>

      {/* Centre-logo editor (dial admins only). The hidden input is driven by the modal's "Upload" button. */}
      {canEditCentreLogo && (
        <>
          <input
            ref={logoInputRef}
            type="file"
            accept="image/png,image/jpeg,image/webp,image/svg+xml,image/gif"
            style={{ display: "none" }}
            onChange={onLogoFile}
          />
          {logoOpen && (
            <div
              role="dialog"
              aria-modal="true"
              aria-label="Change dial logo"
              style={logoOverlay}
              onClick={() => {
                if (!logoBusy) setLogoOpen(false);
              }}
            >
              <div style={logoCard} onClick={(e) => e.stopPropagation()}>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: "var(--text)" }}>Dial logo</h2>
                <p style={{ margin: "6px 0 0", fontSize: 13, color: "var(--text-muted)", lineHeight: 1.5 }}>
                  Choose an image to show in the centre of the dial. It replaces the logo for everyone viewing this
                  dial and stays until you change it again.
                </p>
                <div style={{ display: "flex", justifyContent: "center", margin: "18px 0" }}>
                  <div
                    style={{
                      width: 96,
                      height: 96,
                      borderRadius: "50%",
                      overflow: "hidden",
                      background: "#fff",
                      border: "var(--border-w) solid var(--border)",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {detail.centralImageUrl ? (
                      <img src={detail.centralImageUrl} alt="Current dial logo" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                    ) : (
                      <span style={{ fontSize: 12, color: "var(--text-faint)" }}>No logo</span>
                    )}
                  </div>
                </div>
                {logoError && <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--st-overdue)" }}>{logoError}</p>}
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                  <button disabled={logoBusy} onClick={() => logoInputRef.current?.click()} data-help-anchor={HELP_ANCHORS.logoUpload} className="kd-focusable" style={logoPrimaryBtn}>
                    {logoBusy ? "Working…" : "Upload image…"}
                  </button>
                  {detail.hasCentralImageOverride && (
                    <button disabled={logoBusy} onClick={revertLogo} className="kd-focusable" style={logoSecondaryBtn}>
                      {revertLogoLabel(detail)}
                    </button>
                  )}
                  <button disabled={logoBusy} onClick={() => setLogoOpen(false)} className="kd-focusable" style={{ ...logoSecondaryBtn, marginLeft: "auto" }}>
                    Cancel
                  </button>
                </div>
                <p style={{ margin: "12px 0 0", fontSize: 11.5, color: "var(--text-faint)" }}>PNG, JPG, WebP, SVG or GIF, up to 5 MB.</p>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

const searchRow = {
  display: "flex",
  width: "100%",
  gap: 10,
  alignItems: "center",
  padding: "10px 12px",
  border: "none",
  background: "none",
  borderRadius: "var(--radius-sm)",
  cursor: "pointer",
  font: "inherit",
  textAlign: "left",
  color: "var(--text)",
} as const;

const logoOverlay = {
  position: "fixed",
  inset: 0,
  zIndex: 60,
  background: "rgba(2,6,23,0.45)",
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  padding: 20,
} as const;

const logoCard = {
  width: "100%",
  maxWidth: 420,
  background: "var(--surface)",
  borderRadius: "var(--radius)",
  border: "var(--border-w) solid var(--border)",
  boxShadow: "var(--shadow-lg)",
  padding: "20px 22px",
} as const;

const logoPrimaryBtn = {
  font: "inherit",
  fontSize: 13.5,
  fontWeight: 600,
  padding: "9px 16px",
  borderRadius: "var(--radius-sm)",
  border: "none",
  background: "var(--brand-600, #4338ca)",
  color: "#fff",
  cursor: "pointer",
} as const;

const logoSecondaryBtn = {
  font: "inherit",
  fontSize: 13.5,
  fontWeight: 600,
  padding: "9px 16px",
  borderRadius: "var(--radius-sm)",
  border: "var(--border-w) solid var(--border)",
  background: "var(--surface)",
  color: "var(--text-muted)",
  cursor: "pointer",
} as const;
